import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import { NAVIGATION } from '../../containers/App/constants';
import { peekStoredGiftCardCode } from '../../containers/GiftCard/utils';
import { getStoredReferral } from '../../containers/Referral';
import { getCurrentRootPage, isCrawler } from '../../helpers';
import { isMobileApp } from '../../helpers/app';
import { GiftCardsService } from '../../services/giftCards';
import { IPublicPromoCode } from '../../services/giftCards/types';
import { getLocaleFromPathname, stripLocale } from '../../utils/i18nUrls';

const DISMISSED_KEY = 'tictactrip:promoCodeBannerDismissed';

/** The funnel, where the code is typed, and the back office */
const HIDDEN_ROOT_PAGES = [
  NAVIGATION.INFO,
  NAVIGATION.PAYMENT,
  NAVIGATION.PAYMENT_LINK,
  NAVIGATION.CONFIRMATION,
  NAVIGATION.ADMIN,
];

const Container = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 16px;
  padding: 10px 48px;
  background: #342188;
  color: #ffffff;
  font-size: 14px;
  line-height: 1.35;
  text-align: center;

  @media (max-width: 600px) {
    justify-content: flex-start;
    padding: 8px 44px 8px 16px;
    text-align: left;
    font-size: 13px;
  }
`;

const CodeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: #ffffff;
  color: #342188;
  font: inherit;
  font-weight: 700;
  letter-spacing: 0.06em;
  cursor: pointer;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 50%;
  right: 8px;
  width: 36px;
  height: 36px;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #ffffff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
`;

const isDismissed = (): boolean => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * @description The public promo code of the site, set from the admin, in a banner under the navbar. A click copies the
 * code, to paste at the payment. French pages only: the banner text is written in French. Never in the funnel, the
 * admin, the mobile app or for crawlers, and gone for the session once closed.
 * @returns {JSX.Element | null}
 */
const PromoCodeBanner = () => {
  const { pathname } = useLocation();
  const [promoCode, setPromoCode] = useState<IPublicPromoCode | null>(null);
  const [isClosed, setIsClosed] = useState(isDismissed);
  const [isCopied, setIsCopied] = useState(false);
  const userAgent = window.navigator?.userAgent ?? '';
  const isShown =
    !isClosed &&
    !getLocaleFromPathname(pathname) &&
    !HIDDEN_ROOT_PAGES.includes(getCurrentRootPage(stripLocale(pathname))) &&
    !isCrawler(userAgent) &&
    !isMobileApp(userAgent);

  useEffect(() => {
    if (!isShown || promoCode) return;
    GiftCardsService.getPublicPromoCode()
      .then((response) => setPromoCode(response.promoCode))
      .catch(() => setPromoCode(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShown]);

  // A friend's referral code waiting for the checkout comes first: it is for this visitor only
  const referral = getStoredReferral();
  if (isShown && referral && peekStoredGiftCardCode() === referral.code) {
    return (
      <Container
        aria-label="Parrainage"
        data-testid="referralBanner"
        role="region"
        style={{ background: '#0B7A57' }}
      >
        <span>
          {referral.firstName || 'Un proche'} vous offre{' '}
          <strong>
            {(referral.amountCents / 100).toLocaleString('fr-FR')} €
          </strong>{' '}
          sur votre premier voyage
        </span>
        <span style={{ fontSize: 12, color: '#CFF3E6' }}>
          appliqué au paiement
        </span>
      </Container>
    );
  }

  if (!isShown || !promoCode) return null;

  const close = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // The banner closes for this page only
    }
    setIsClosed(true);
  };

  return (
    <Container
      aria-label="Offre du moment"
      data-testid="promoCodeBanner"
      role="region"
    >
      <span>{promoCode.bannerText}</span>
      <CodeButton
        aria-label={`Copier le code ${promoCode.code}`}
        onClick={() =>
          navigator.clipboard
            ?.writeText(promoCode.code)
            .then(() => setIsCopied(true))
            .catch(() => setIsCopied(false))
        }
        type="button"
      >
        {promoCode.code}
        <svg
          aria-hidden="true"
          fill="none"
          height="13"
          stroke="currentColor"
          strokeWidth="2.2"
          viewBox="0 0 24 24"
          width="13"
        >
          <path d="M8 8h12v12H8zM16 8V4H4v12h4" />
        </svg>
      </CodeButton>
      {isCopied && (
        <span role="status" style={{ fontSize: 12, color: '#D8D2FF' }}>
          Copié, à saisir au paiement
        </span>
      )}
      <CloseButton aria-label="Fermer le bandeau" onClick={close} type="button">
        ×
      </CloseButton>
    </Container>
  );
};

export default PromoCodeBanner;
